import { getAccessToken } from '../authentication/supabaseService';
import type { CustomIngredient } from '../customIngredients/customIngredientsService';
import { isPracticeId, practiceRestaurantId } from '../../components/onboarding/practice/practiceGuards';

export interface AutomationRun {
  id: string;
  restaurantId: string;
  name: string;
  fileName: string | null;
  dishCount: number;
  ingredientLineCount: number;
  completedAt: string | null;
  createdAt: string;
}

export class AutomationRunApiError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
    this.name = 'AutomationRunApiError';
  }
}

const inPractice = (restaurantId: string) => practiceRestaurantId() === restaurantId || isPracticeId(restaurantId);

async function request<T>(url: string, method = 'GET', body?: object): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok) throw new AutomationRunApiError(data.error || 'request_failed', response.status);
  return data;
}

export async function getOpenAutomationRuns(restaurantId: string): Promise<AutomationRun[]> {
  if (inPractice(restaurantId)) return [];
  return request(`/api/costprice/automation-runs?restaurantId=${encodeURIComponent(restaurantId)}`);
}

function requireLive(restaurantId: string, ...ids: string[]) {
  if (inPractice(restaurantId) || ids.some(isPracticeId)) throw new AutomationRunApiError('practice_unavailable', 400);
}

export async function setDishReviewed(runId: string, restaurantId: string, costPriceId: string, reviewed: boolean) {
  requireLive(restaurantId, runId, costPriceId);
  return request<{ id: string; automationReviewedAt: string | null }>(
    `/api/costprice/automation-runs/${encodeURIComponent(runId)}/dishes/${encodeURIComponent(costPriceId)}/reviewed`,
    'PUT', { restaurantId, reviewed },
  );
}

export async function setDishReviewFlag(runId: string, restaurantId: string, costPriceId: string, changes: { flagged?: boolean; note?: string | null }) {
  requireLive(restaurantId, runId, costPriceId);
  return request<{ id: string; automationReviewFlagged: boolean; automationReviewNote: string | null }>(
    `/api/costprice/automation-runs/${encodeURIComponent(runId)}/dishes/${encodeURIComponent(costPriceId)}/review-flag`,
    'PUT', { restaurantId, ...changes },
  );
}

export async function completeAutomationRun(runId: string, restaurantId: string) {
  requireLive(restaurantId, runId);
  return request<{ completedAt: string }>(`/api/costprice/automation-runs/${encodeURIComponent(runId)}/complete`, 'PUT', { restaurantId });
}

export async function ensureNameOnlyCustomIngredient(restaurantId: string, name: string, unit: 'g' | 'ml' | 'p'): Promise<CustomIngredient> {
  requireLive(restaurantId);
  return request('/api/customingredients/ensure-name-only', 'POST', { restaurantId, name, unit });
}
